/**
 * Weather Warnings Analyzer
 * Analyzes weather data from localities and generates appropriate warnings
 */

import {
  WarningTypes,
  WarningSeverity,
  dangerousWeatherCodes,
  warningThresholds,
  warningMessages,
} from '../config/weatherWarnings';

/**
 * Determines the severity level based on a value and thresholds
 * @param {number} value - The value to check
 * @param {object} thresholds - The threshold configuration
 * @returns {string|null} - The severity level or null if below info threshold
 */
function getSeverityFromValue(value, thresholds) {
  if (value >= thresholds.extreme) return WarningSeverity.EXTREME;
  if (value >= thresholds.danger) return WarningSeverity.DANGER;
  if (value >= thresholds.warning) return WarningSeverity.WARNING;
  if (value >= thresholds.info) return WarningSeverity.INFO;
  return null;
}

/**
 * Analyzes a single hour of weather data for warnings
 * @param {object} hourData - Weather data for a specific hour
 * @returns {Array} - Array of warnings found
 */
function analyzeHourForWarnings(hourData) {
  const warnings = [];

  if (!hourData) return warnings;

  const {
    weatherCode,
    temperature,
    windSpeed,
    windGusts,
    precipitation,
    stormProbability,
    snowProbability,
  } = hourData;

  // Check for snow conditions by weather code
  if (weatherCode && dangerousWeatherCodes.snow.includes(weatherCode)) {
    const severity = snowProbability
      ? getSeverityFromValue(
          parseFloat(snowProbability),
          warningThresholds.snowProbability
        )
      : WarningSeverity.WARNING;

    if (severity) {
      warnings.push({
        type: WarningTypes.SNOW,
        severity: severity || WarningSeverity.WARNING,
        weatherCode,
      });
    }
  }

  // Check for storm conditions by weather code
  if (weatherCode && dangerousWeatherCodes.storm.includes(weatherCode)) {
    const stormProb = stormProbability?.value || stormProbability;
    const severity = stormProb
      ? getSeverityFromValue(
          parseFloat(stormProb),
          warningThresholds.stormProbability
        )
      : WarningSeverity.WARNING;

    warnings.push({
      type: WarningTypes.STORM,
      severity: severity || WarningSeverity.WARNING,
      weatherCode,
    });
  }

  // Check for heavy rain by weather code
  if (weatherCode && dangerousWeatherCodes.heavyRain.includes(weatherCode)) {
    const severity = precipitation
      ? getSeverityFromValue(
          parseFloat(precipitation),
          warningThresholds.precipitation
        )
      : WarningSeverity.WARNING;

    warnings.push({
      type: WarningTypes.HEAVY_RAIN,
      severity: severity || WarningSeverity.WARNING,
      precipitation,
    });
  }

  // Check for fog
  if (weatherCode && dangerousWeatherCodes.fog.includes(weatherCode)) {
    warnings.push({
      type: WarningTypes.FOG,
      severity: WarningSeverity.WARNING,
      weatherCode,
    });
  }

  // Check wind speed
  if (windSpeed) {
    const windValue = parseFloat(windSpeed);
    const severity = getSeverityFromValue(windValue, warningThresholds.wind);
    if (severity) {
      warnings.push({
        type: WarningTypes.STRONG_WIND,
        severity,
        windSpeed: windValue,
      });
    }
  }

  // Check wind gusts
  if (windGusts) {
    const gustValue = parseFloat(windGusts);
    const severity = getSeverityFromValue(
      gustValue,
      warningThresholds.windGusts
    );
    if (severity && severity !== WarningSeverity.INFO) {
      // Only add if more severe than regular wind warning
      const existingWindWarning = warnings.find(
        w => w.type === WarningTypes.STRONG_WIND
      );
      if (
        !existingWindWarning ||
        severityToNumber(severity) >
          severityToNumber(existingWindWarning.severity)
      ) {
        // Remove existing wind warning and add new one with higher severity
        const index = warnings.findIndex(
          w => w.type === WarningTypes.STRONG_WIND
        );
        if (index > -1) warnings.splice(index, 1);
        warnings.push({
          type: WarningTypes.STRONG_WIND,
          severity,
          windGusts: gustValue,
        });
      }
    }
  }

  // Check for ice risk (low temperature + precipitation or humidity)
  if (temperature) {
    const tempValue = parseFloat(temperature);
    if (tempValue <= warningThresholds.temperature.cold) {
      // Risk of ice
      let severity = WarningSeverity.INFO;
      if (tempValue <= warningThresholds.temperature.extremeCold) {
        severity = WarningSeverity.DANGER;
      } else if (tempValue <= 0) {
        severity = WarningSeverity.WARNING;
      }

      warnings.push({
        type: WarningTypes.ICE,
        severity,
        temperature: tempValue,
      });
    }

    // Check for extreme cold
    if (tempValue <= warningThresholds.temperature.extremeCold) {
      warnings.push({
        type: WarningTypes.EXTREME_COLD,
        severity:
          tempValue <= -10 ? WarningSeverity.EXTREME : WarningSeverity.DANGER,
        temperature: tempValue,
      });
    }

    // Check for extreme heat
    if (tempValue >= warningThresholds.temperature.hot) {
      let severity = WarningSeverity.WARNING;
      if (tempValue >= warningThresholds.temperature.extremeHot) {
        severity = WarningSeverity.EXTREME;
      } else if (tempValue >= 38) {
        severity = WarningSeverity.DANGER;
      }

      warnings.push({
        type: WarningTypes.EXTREME_HEAT,
        severity,
        temperature: tempValue,
      });
    }
  }

  // Check storm probability independently
  if (stormProbability) {
    const probValue = parseFloat(stormProbability?.value || stormProbability);
    const severity = getSeverityFromValue(
      probValue,
      warningThresholds.stormProbability
    );
    if (severity && !warnings.find(w => w.type === WarningTypes.STORM)) {
      warnings.push({
        type: WarningTypes.STORM,
        severity,
        probability: probValue,
      });
    }
  }

  return warnings;
}

/**
 * Convert severity to numeric value for comparison
 */
function severityToNumber(severity) {
  switch (severity) {
    case WarningSeverity.EXTREME:
      return 4;
    case WarningSeverity.DANGER:
      return 3;
    case WarningSeverity.WARNING:
      return 2;
    case WarningSeverity.INFO:
      return 1;
    default:
      return 0;
  }
}

/**
 * Analyzes all localities and their weather forecasts for warnings
 * @param {Array} localities - Array of localities with weather data
 * @returns {object} - Object containing warnings summary and details
 */
export function analyzeRouteForWarnings(localities) {
  if (!localities || localities.length === 0) {
    return { hasWarnings: false, warnings: [], summary: {} };
  }

  const allWarnings = [];
  const warningsByLocality = {};
  const warningsSummary = {};

  localities.forEach((locality, index) => {
    if (!locality.weatherForecast) return;

    const localityWarnings = [];

    // Analyze each day's forecast
    locality.weatherForecast.forEach((day, dayIndex) => {
      if (!day.hours) return;

      // Analyze each hour
      day.hours.forEach((hour, hourIndex) => {
        const hourWarnings = analyzeHourForWarnings(hour);

        hourWarnings.forEach(warning => {
          // Add context to warning
          const enrichedWarning = {
            ...warning,
            localityName: locality.localityName,
            localityProvince: locality.localityProvince,
            localityIndex: index,
            dayIndex,
            hourIndex,
            period: hour.period,
            passTime: locality.passTime,
          };

          localityWarnings.push(enrichedWarning);
          allWarnings.push(enrichedWarning);

          // Update summary
          if (!warningsSummary[warning.type]) {
            warningsSummary[warning.type] = {
              count: 0,
              maxSeverity: warning.severity,
              localities: new Set(),
            };
          }
          warningsSummary[warning.type].count++;
          warningsSummary[warning.type].localities.add(locality.localityName);

          // Update max severity
          if (
            severityToNumber(warning.severity) >
            severityToNumber(warningsSummary[warning.type].maxSeverity)
          ) {
            warningsSummary[warning.type].maxSeverity = warning.severity;
          }
        });
      });
    });

    if (localityWarnings.length > 0) {
      warningsByLocality[locality.localityName] = localityWarnings;
    }
  });

  // Convert Set to Array in summary
  Object.keys(warningsSummary).forEach(type => {
    warningsSummary[type].localities = Array.from(
      warningsSummary[type].localities
    );
  });

  // Get unique warnings (deduplicate by type and max severity)
  const uniqueWarnings = getUniqueWarnings(allWarnings);

  // Sort by severity (most severe first)
  uniqueWarnings.sort(
    (a, b) => severityToNumber(b.severity) - severityToNumber(a.severity)
  );

  return {
    hasWarnings: allWarnings.length > 0,
    warnings: uniqueWarnings,
    allWarnings,
    warningsByLocality,
    summary: warningsSummary,
    highestSeverity:
      uniqueWarnings.length > 0 ? uniqueWarnings[0].severity : null,
  };
}

/**
 * Get unique warnings by type, keeping the highest severity
 */
function getUniqueWarnings(warnings) {
  const uniqueMap = new Map();

  warnings.forEach(warning => {
    const existing = uniqueMap.get(warning.type);
    if (
      !existing ||
      severityToNumber(warning.severity) > severityToNumber(existing.severity)
    ) {
      uniqueMap.set(warning.type, warning);
    }
  });

  return Array.from(uniqueMap.values());
}

/**
 * Get unique warnings for a specific locality
 * @param {object} warningsAnalysis - The full warnings analysis object
 * @param {string} localityName - The name of the locality
 * @returns {object|null} - Object with unique warnings for this locality
 */
export function getUniqueWarningsForLocality(warningsAnalysis, localityName) {
  if (!warningsAnalysis || !localityName) return null;

  const localityWarnings = warningsAnalysis.warningsByLocality?.[localityName];
  if (!localityWarnings || localityWarnings.length === 0) return null;

  // Get unique warnings by type for this locality
  const uniqueWarnings = getUniqueWarnings(localityWarnings);

  // Sort by severity (most severe first)
  uniqueWarnings.sort(
    (a, b) => severityToNumber(b.severity) - severityToNumber(a.severity)
  );

  return {
    count: uniqueWarnings.length,
    maxSeverity: uniqueWarnings.length > 0 ? uniqueWarnings[0].severity : null,
    warnings: uniqueWarnings,
  };
}

/**
 * Gets the warning message for a specific warning
 * @param {object} warning - The warning object
 * @returns {object} - Object with title and message
 */
export function getWarningMessage(warning) {
  const messages = warningMessages[warning.type];
  if (!messages) {
    return {
      title: 'Alerta Meteorológica',
      message:
        'Se han detectado condiciones meteorológicas adversas en tu ruta.',
    };
  }

  return {
    title: messages.title,
    message: messages[warning.severity] || messages[WarningSeverity.WARNING],
  };
}

/**
 * Check if any warning requires immediate attention (DANGER or EXTREME)
 */
export function hasUrgentWarnings(warningsAnalysis) {
  return warningsAnalysis.warnings.some(
    w =>
      w.severity === WarningSeverity.DANGER ||
      w.severity === WarningSeverity.EXTREME
  );
}

/**
 * Get a brief summary text for all warnings
 */
export function getWarningsSummaryText(warningsAnalysis) {
  if (!warningsAnalysis.hasWarnings) {
    return 'No se han detectado alertas meteorológicas en tu ruta.';
  }

  const { warnings, highestSeverity } = warningsAnalysis;
  const count = warnings.length;

  let severityText = '';
  switch (highestSeverity) {
    case WarningSeverity.EXTREME:
      severityText = 'críticas';
      break;
    case WarningSeverity.DANGER:
      severityText = 'peligrosas';
      break;
    case WarningSeverity.WARNING:
      severityText = 'moderadas';
      break;
    default:
      severityText = 'leves';
  }

  return `Se han detectado ${count} alerta${count > 1 ? 's' : ''} meteorológica${count > 1 ? 's' : ''} ${severityText} en tu ruta.`;
}
